import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { getDocumentHelper } from '../../../../actions/utility-actions';
import { PageBody, PageContent, PageHeading } from '../../../../components/page-components/_index';
import { PagedList } from '../../../../components/paged-list/PagedList';
import { HasPermission } from '../../../../containers/authorization-helpers/HasPermission';
import { OrderHistory } from '../../../../models/ordering/order-history';
import { SearchOrderSummary } from '../../../../models/ordering/search-order-summary';
import { PagedApiResult } from '../../../../models/utilities/paged-request';
import { StatusCode } from '../../../../utils/api';
import { PageNames } from '../../../../utils/app-constants';
import { formatCurrency } from '../../../../utils/currency-formatter';
import { OrderDetailFlyout } from '../../../../modules/ordering/components/OrderDetailFlyout';
import { OrderProgress } from '../components/OrderProgress';
import { DocumentOrderQueryExport } from '../../../../modules/ordering/models/order-query-document';
import { hasSearchCriteria, OrderSearchFilters } from '../../../../modules/ordering/models/order-search-filters';
import { OrderingSvc } from '../../../../modules/ordering/service';
import { PendingReason } from '../../../../models/ordering/enums/pending-reason';
import { OrderStatus, OrderStatusMetadata } from '../../../../models/ordering/enums/order-status';
import { ChiTooltip } from '../../../../components/utility/ChiTooltip';

export const OrdersScene = () => {

    const dispatch = useDispatch();
    const [service] = React.useState(new OrderingSvc());
    const [filterParams, setFilterParams] = React.useState(new OrderSearchFilters());
    const [isFlyoutOpen, setIsFlyoutOpen] = React.useState(false);
    const [orderObj, setOrderObj] = React.useState<any>({});
    const [orderHistory, setOrderHistory] = React.useState<any>([]);
    const [hasCriteria, setHasCriteria] = React.useState(false);

    const [orders, setOrders] = React.useState(() => {
         const initial = new PagedApiResult<SearchOrderSummary>();
         initial.pagination = filterParams.paginationInfo;
         initial.pagination.reusePaging = true;
         return initial;
    });

    /**
     * OnSearch handler
     * @param filters The user search filters to apply
     */
    const onSearch = async (filters: OrderSearchFilters) => {
        const criteriaSet = hasSearchCriteria(filters);
        setHasCriteria(criteriaSet);

        if (criteriaSet) {
            const response = await service.searchOrders(filters);
            if (response.status === StatusCode.Success) {
                setOrders(response.data);
                scrollTo();
            }
        }
        else {
            // Don't load every order, wait until the user picks something to filter on
            const cleared = new PagedApiResult<SearchOrderSummary>();
            cleared.pagination = filters.paginationInfo;
            setOrders(cleared);
        }

        setFilterParams(filters);
    };

    const onParamsUpdate = (itm : any) => {
        setFilterParams(itm);
    };

    const onParamsReset = () => {
        const tmp = new OrderSearchFilters();
        setFilterParams(tmp);
    };

    // Exporting every order is too much, so wait until the user has picked something to filter on
    const canExport = hasSearchCriteria(filterParams);

    const exportQuery = async () => {
        const req = new DocumentOrderQueryExport(filterParams);
        dispatch(getDocumentHelper(req));
    };

    const getOrderDetails = async (orderId : string) => {

        let shouldOpen = true;
        
        const detailsResult = await service.getOrder(orderId);
        if (detailsResult.status === StatusCode.Success) {
            setOrderObj(detailsResult.data);
        }
        else {
            setOrderObj({});
            shouldOpen = false;
        }
        
        const historyResult = await service.getOrderHistory(orderId);
        if (historyResult.status === StatusCode.Success) {
            setOrderHistory(historyResult.data);
        }
        else {
            setOrderHistory({} as unknown as OrderHistory);
            shouldOpen = false;
        }

        if (shouldOpen) {
            setIsFlyoutOpen(true);
        }

    };

    const getPendingReasons = (pendingReason: PendingReason) => {
        const reasons: string[] = [];
        
        if (pendingReason & PendingReason.CreditHold) {
            reasons.push('Credit Hold');
        }
        if (pendingReason & PendingReason.EngineeringReview) {
            reasons.push('Engineering Review');
        }
        if (pendingReason & PendingReason.WindLoadReview) {
            reasons.push('Wind Load Review');
        }
        if (pendingReason & PendingReason.CustomerServiceReview) {
            reasons.push('Customer Service Review');
        }
        if (pendingReason & PendingReason.AddressReview) {
            reasons.push('Address Review');
        }

        return reasons;
    };

    /**
     * The name of the status of the order.  A pending order says, when hovered over, what it is waiting on.
     */
    const renderStatus = (order: SearchOrderSummary) => {
        const text = OrderStatusMetadata[order.status]?.display ?? `${order.status}`;
        const reasons = order.status === OrderStatus.Pending ? getPendingReasons(order.pendingReason) : [];

        if (reasons.length === 0) {
            return text;
        }

        const reasonText = reasons.length === 1 ? 'reason' : 'reasons';
        return <ChiTooltip
            title={ `Status: ${text}` }
            body={ <p>{ `The order is pending due to the following ${reasonText}: ${reasons.join(', ')}.` }</p> }
            content={ <span>{ text }</span> } />;
    };

    /**
     * Close the flyout
     */
    const closeFlyout = () => {
        setIsFlyoutOpen(false);
        setOrderObj({});
        setOrderHistory([]);
    };
    
    return <PageContent pageName={PageNames.ORDERING_ORDER_QUERY}>
            <PageHeading>
                <HasPermission requiredPermissions={[
                    Permissions.OrderingOrders
                ]}>
                    <button className="btn new attention" onClick={ exportQuery } disabled={ !canExport }>
                        Export
                    </button>
                </HasPermission>
            </PageHeading>
            <PageBody>
                <PagedList
                    noResultsMessage={ hasCriteria ? "No orders found, please search again." : "Select at least one filter to see results." }
                    searchCallback={ onSearch }
                    data={ orders }
                    filterProps={ filterParams }
                    selectableClass=""
                    onFilterUpdate={ onParamsUpdate }
                    onReset={ onParamsReset }
                    rowRenderer={(itm: SearchOrderSummary) => 
                        <tr onClick={ () => getOrderDetails(itm.id) } key={ itm.id }>
                            <td><a>{ itm.id }</a></td>
                            <td>{ renderStatus(itm) }</td>
                            <td>
                                <OrderProgress order={ itm } />
                            </td>
                            <td>{ itm.quantity }</td>
                            <td>{ itm.description }</td>
                            <td>{ itm.poNumber }</td>
                            <td>{ itm.jobName }</td>
                            <td>{ formatCurrency(itm.amount) }</td>
                        </tr>
                    }
                />     

                <OrderDetailFlyout
                    isOpen={ isFlyoutOpen }
                    onClose={ closeFlyout }
                    orderObj={ orderObj }
                    orderHistory={ orderHistory }
                />

            </PageBody>
        </PageContent>;
};
